<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Immediate Families"
      text="Update immediate family"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="localDependent.name"
                v-validate="'required|max:80|nameAllowedFormat'"
                label="Name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"  
                outlined
                :class="{
                  'required':!localDependent.name
                }"
                @input="localDependent.name = localDependent.name.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <span> Citizenship? </span>
              <v-radio-group
                v-model="localDependent.is_citizen"
                class="mt-0"
                row
                @change="localDependent.ic=null,localDependent.passport_no=null"
              >
                <v-radio
                  label="Non-citizen"
                  :value="0"
                />
                <v-radio
                  label="Citizen"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-text-field 
                v-show="localDependent.is_citizen === 1"
                v-model="localDependent.ic"
                v-validate="localDependent.is_citizen === 1 ? 'required|icDate|icLength' : ''"
                v-mask="'######-##-####'"
                label="IC"
                :error-messages="errors.collect('ic')"
                data-vv-name="ic"
                data-vv-as="ic"
                outlined
                :class="{
                  'required':!localDependent.ic && localDependent.is_citizen === 1
                }"
                @input="getBirthdayFromIC(localDependent.ic)"
              />
              <v-text-field
                v-show="localDependent.is_citizen === 0"
                v-model="localDependent.passport_no"
                v-validate="localDependent.is_citizen === 0 ? 'required' : ''"
                label="Passport No."
                :error-messages="errors.collect('passport_no')"
                data-vv-name="passport_no"
                data-vv-as="passport no"
                outlined
                :class="{
                  'required':!localDependent.passport_no && localDependent.is_citizen === 0
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-menu
                v-model="birthDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }"> 
                  <v-text-field
                    v-model="birthDateDisplay"
                    v-validate="'required'"
                    label="Birth Date"
                    :error-messages="errors.collect('birthDateDisplay')"
                    data-vv-name="birthDateDisplay"
                    data-vv-as="birth date"
                    readonly
                    outlined
                    :class="{
                      'required':!birthDateDisplay
                    }"
                    class="label-position"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localDependent.birth_date"
                  @input="birthDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localDependent.gender_lookup_uid"
                v-validate="'required'"
                :error-messages="errors.collect('gender')"
                :items="gender"
                data-vv-name="gender"
                data-vv-as="gender"
                item-text="value"
                item-value="lookup_uid"
                label="Gender"
                outlined
                :class="{
                  'required':!localDependent.gender_lookup_uid
                }"
              >
                <template v-slot:prepend-inner>
                  <v-icon>
                    mdi-human-male-female
                  </v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localDependent.relationship_id"
                v-validate="'required'"
                :error-messages="errors.collect('relationship_id')"
                :items="relationships"
                data-vv-name="relationship_id"
                data-vv-as="relationship"
                item-text="name"
                item-value="id"
                label="Relationship"
                outlined
                :class="{
                  'required':!localDependent.relationship_id
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localDependent.relation_status_id"
                v-validate="'required'"
                :error-messages="errors.collect('relationStatus')"
                :items="relationStatuses"
                data-vv-name="relationStatus"
                data-vv-as="relation status"
                item-text="name"
                item-value="id"
                label="Relation Status"
                outlined
                :class="{
                  'required':!localDependent.relation_status_id
                }"
                @change="localDependent.company_name = null;localDependent.income_tax_no = null;localDependent.occupation = null"
              >
                <template v-slot:item="data">
                  <v-list-item-content>
                    <span class="font-weight-bold">{{ data.item.name }}</span>
                    <span style="width:100px">{{ data.item.description }}</span>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col
              v-show="localDependent.relation_status_id === 4"
              cols="6"
              class="pb-0"
            >
              <v-text-field
                v-model="localDependent.company_name"
                v-validate="localDependent.relation_status_id === 4 ? 'required' : ''"
                label="Company Name"
                :error-messages="errors.collect('company_name')"
                data-vv-name="company_name"
                data-vv-as="company name"
                outlined
              />
            </v-col>

            <v-col
              v-show="localDependent.relation_status_id === 4"
              cols="6"
              class="pb-0"
            >
              <v-text-field
                v-model="localDependent.occupation"
                v-validate="localDependent.relation_status_id === 4 ? 'required' : ''"
                label="Occupation"
                :error-messages="errors.collect('occupation')"
                data-vv-name="occupation"
                data-vv-as="occupation"
                outlined
              />
            </v-col>

            <v-col
              :cols="localDependent.relation_status_id === 1 || localDependent.relation_status_id === 3 ? 12 : 6"
              class="pb-0"
            >
              <span> Disabled? </span>
              <v-radio-group
                v-model="localDependent.is_disabled"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>

            <v-col
              v-if="localDependent.relation_status_id === 2"
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localDependent.education_level"
                v-validate="localDependent.relation_status_id === 2 ? 'required' : ''"
                :error-messages="errors.collect('education_level')"
                :items="educationLevels"
                data-vv-name="educationLevel"
                data-vv-as="education level"
                item-text="name"
                item-value="key"
                label="Education Level"
                outlined
              />
            </v-col>

            <v-col
              v-show="localDependent.relation_status_id === 4"
              cols="6"
              class="pb-0"
            >
              <v-text-field
                v-model="localDependent.income_tax_no"
                v-validate="'length:13|validAlphanumeric'"
                :error-messages="errors.collect('incomeTaxNumber')"
                data-vv-name="incomeTaxNumber"
                data-vv-as="Income tax number"
                label="Income Tax No."
                outlined
                @input="localDependent.income_tax_no = localDependent.income_tax_no.toUpperCase()"
              />
            </v-col>

            <v-col
              v-if="localDependent.relationship_id === 2"
              cols="3"
              class="pb-0"
            >
              <span> Tax Relief? </span>
              <v-radio-group
                v-model="localDependent.is_tax_relief"
                class="mt-0"
                row
                @change="localDependent.is_tax_relief?localDependent.tax_relief_percentage = 100 :localDependent.tax_relief_percentage = 0"
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>
            <v-col
              v-if="localDependent.relationship_id === 2 && localDependent.is_tax_relief"
              cols="3"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localDependent.tax_relief_percentage"
                :error-messages="errors.collect('taxReliefPercentage')"
                :items="percentages"
                data-vv-name="taxReliefPercentage"     
                data-vv-as="taxReliefPercentage"
                item-text="name"
                item-value="key"
                label="Percentage"
              />
            </v-col>
          </v-row>
          <v-row
            v-if="(localDependent.relationship_id === 1 || localDependent.relationship_id === 2) 
              && (
                localDependent.relationship_id === dependent.relationship_id
                && (localDependent.relation_status_id !== dependent.relation_status_id 
                  || localDependent.education_level !== dependent.education_level
                  || localDependent.is_disabled !== dependent.is_disabled
                  || localDependent.tax_relief_percentage !== dependent.tax_relief_percentage)
              )"
          >
            <v-col cols="12">
              <v-divider cols="12" />
              <v-divider cols="12" />
              <v-card>
                <v-col cols="12">
                  <v-card-text>
                    <v-card-title class="pl-0 mt-n5">
                      Original Record
                    </v-card-title>
                    <v-row no-gutters>
                      <v-col cols="3">
                        <span class="font-weight-bold"> Relation Status </span>
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col cols="8">
                        {{ dependent.relation_status.name }}
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="3">
                        <span class="font-weight-bold"> Education Level </span>
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col cols="8">
                        {{ !dependent.education_level || dependent.education_level === 'null' ? 'N/A' : dependent.education_level }}
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="3">
                        <span class="font-weight-bold"> Is Disabled? </span>
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col cols="8">
                        {{ dependent.is_disabled ? 'Yes' : 'No' }}
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="3">
                        <span class="font-weight-bold"> Has Tax Relief? </span>
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col cols="8">
                        {{ dependent.is_tax_relief ? 'Yes (' + dependent.tax_relief_percentage + '%)' : 'No' }}
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-col>
              </v-card>
              <v-checkbox
                v-model="requestSaveHistory"
              >
                <template v-slot:label>
                  <div>
                    Save original record as history
                  </div>
                </template>
              </v-checkbox>
            </v-col>
            <v-col
              v-if="requestSaveHistory"
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="startYear"
                v-validate="requestSaveHistory ? 'required' : ''"
                :items="years"
                :error-messages="errors.collect('startYear')"
                data-vv-name="startYear"     
                data-vv-as="start year"
                label="Original Record Payroll Start Year"
                item-text="value"
                item-value="id"
                outlined
              />
            </v-col>

            <v-col
              v-if="requestSaveHistory"
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="endYear"
                v-validate="requestSaveHistory ? 'required' : ''"
                :items="years"
                :error-messages="errors.collect('endYear')"
                data-vv-name="endYear"     
                data-vv-as="end year"
                label="Original Record Payroll End Year"
                item-text="value"
                item-value="id"
                outlined
              />
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card> 
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      dependent: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        requestSaveHistory: true,
        startYear: null,
        endYear: null,
        years: [],
        localDependent: null,
        relationships: [],
        relationStatuses: [],
        loading: false,
        percentages: [
          { key: 50, name: '50 %' },
          { key: 100, name: '100 %' },
        ],
        birthDateMenu: false,
        educationLevels: [
          { key: 'PRIMARY', name: 'Primary' },
          { key: 'SECONDARY', name: 'Secondary' },
          { key: 'ALEVEL', name: 'A-Level' },
          { key: 'CERTIFICATE', name: 'Certificate' },
          { key: 'MATRICULATION', name: 'Matriculation' },
          { key: 'PREPARATORY_COURSES', name: 'Preparatory Courses' },
          { key: 'DIPLOMA', name: 'Diploma in Malaysia (excluding matriculation/ preparatory courses)' },
          { key: 'OVERSEAS_DIPLOMA', name: 'Overseas Diploma' },
          { key: 'DEGREE', name: 'Degree in Malaysia' },
          { key: 'OVERSEAS_DEGREE', name: 'Overseas Degree' },
          { key: 'MASTER', name: 'Master in Malaysia' },
          { key: 'OVERSEAS_MASTER', name: 'Overseas Master' },
          { key: 'DOCTORATE', name: 'Doctorate in Malaysia' },
          { key: 'OVERSEAS_DOCTORATE', name: 'Overseas Doctorate' },
        ],
      }
    },
    computed: {
      ...mapState('lookup', ['gender']),

      birthDateDisplay () {
        return this.localDependent.birth_date ? this.$moment(this.localDependent.birth_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return

        this.loading = true

        this.localDependent = { ...this.dependent }
     
        this.$validator.extend('icDate', {
          getMessage (field, args) {
            return 'Invalid IC Date'        
          },
          validate (value, args) {
            var y = parseInt(value.substr(0, 2))
            var m = parseInt(value.substr(2, 2))
            var d = parseInt(value.substr(4, 2))
            if (m === 0 || m > 12) { return false }
            var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ]
            // Adjust for leap years
            if (y % 400 === 0 || (y % 100 !== 0 && y % 4 === 0)) { monthLength[1] = 29 }
            return d > 0 && d <= monthLength[m - 1]
          },
        })
        this.$validator.extend('icLength', {
          getMessage (field, args) {
            return 'IC not in right length'        
          },
          validate (value, args) {
            return value.toString().length === 14
          },
        })
        this.$validator.extend('nameAllowedFormat', {
          getMessage (field, args) {
            return "Invalid name format. Only alphabet and special character of @ ' * - / . allowed for name"     
          },
          validate (value, args) {
            const regex = /^[ A-Za-z'@./*-]*$/
            return regex.test(value)
          },
        }) 
        this.$validator.extend('validAlphanumeric', {
          getMessage (field, args) {
            return 'Income tax number should not contain any special character'     
          },
          validate (value, args) {
            const regex = /^[A-Z0-9]*$/
            return regex.test(value)
          },
        })          

        Promise.all([
          this.$api.hrisRelationships().list(),
          this.$api.hrisRelationStatuses().list(),
          this.$api.taxReliefs().listYears(),
        ])
          .then(([employee, relationStatus, years]) => {
            this.relationships = employee.data
            this.relationStatuses = relationStatus.data
            this.years = years.data.filter(year => {
              return year < this.$moment().format('YYYY')
            })

            this.loading = false
          })
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)

            this.$api.hrisDependents().update(this.dependent.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false
                }
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        if (this.localDependent.relation_status_id !== 4) {
          this.localDependent.company_name = null
          this.localDependent.occupation = null
          this.localDependent.income_tax_no = null
        }

        if (this.localDependent.relation_status_id !== 2) {
          this.localDependent.education_level = null
        }

        if (!this.requestSaveHistory) {
          this.startYear = null
          this.endYear = null
        }
        
        return {
          name: this.localDependent.name,
          is_citizen: this.localDependent.is_citizen,
          ic: this.localDependent.ic ? this.localDependent.ic.replace(/-/g, '') : '',
          passport_no: this.localDependent.passport_no ? this.localDependent.passport_no : '',
          gender_lookup_uid: this.localDependent.gender_lookup_uid,
          relationship_id: this.localDependent.relationship_id,
          company_name: this.localDependent.company_name ? this.localDependent.company_name : '',
          relation_status_id: this.localDependent.relation_status_id,
          birth_date: this.localDependent.birth_date,
          is_disabled: this.localDependent.is_disabled,
          occupation: this.localDependent.occupation ? this.localDependent.occupation : '',
          is_tax_relief: this.localDependent.relationship_id === 2 ? this.localDependent.is_tax_relief : 1,
          tax_relief_percentage: this.localDependent.is_tax_relief ? this.localDependent.tax_relief_percentage : (this.localDependent.relationship_id === 1 ? 100 : 0),
          income_tax_no: this.localDependent.income_tax_no ? this.localDependent.income_tax_no : '',
          education_level: this.localDependent.education_level,
          start_year: this.startYear,
          end_year: this.endYear,
        }
      },

      getBirthdayFromIC (value) {
        let ic = value.replace(/-/g, '')

        if (ic.length === 12) {
          let date = value.substr(0, 6)
     
          if (Number(date.substr(0, 2)) >= 48) {
            this.localDependent.birth_date = '19' + date.substr(0, 2) + '-' + date.substr(2, 2) + '-' + date.substr(4, 2)
          } else {
            this.localDependent.birth_date = '20' + date.substr(0, 2) + '-' + date.substr(2, 2) + '-' + date.substr(4, 2)
          }

          let lastDigitIc = value.substr(value.length - 1, 1)
  
          if (['0', '2', '4', '6', '8'].includes(lastDigitIc)) {
            this.localDependent.gender_lookup_uid = '1c66161e-dbe1-11e7-ba92-509a4c2dfbad'
          } else {
            this.localDependent.gender_lookup_uid = '1c661585-dbe1-11e7-ba92-509a4c2dfbad'
          }
        }
      },
    },
  }
</script>

<style scoped>
.required >>> fieldset {
  border: 2px solid red;
}

.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:1px
}
</style>
